import clsx from 'clsx';
import React from 'react';
import {Stack} from '@mui/material';
import {
  Avatar,
  Body,
  Label,
  designSystemToken,
} from '@lightricks/react-design-system';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import {Message} from '@/api/messages/types';
import {NegotiationResponse} from '@/api/messages';
import MessageAttachmentTile from './MessageAttachmentTile';
import NegotiationSnapshotCard from './NegotiationSnapshotCard';
import styles from './MessageListItem.module.scss';

interface Props {
  message: Message;
  onRespondToNegotiation?: (
    action: NegotiationResponse,
    brandNegotiationId: string
  ) => Promise<void>;
}

function initialFor(message: Message): string {
  const source = message.displayName || message.sender?.name || '';
  return source.charAt(0).toUpperCase();
}

export default function MessageListItem({
  message,
  onRespondToNegotiation,
}: Props) {
  const {fromCurrentUser} = message;
  const imageUrl = message.sender?.imageUrl ?? undefined;
  const formattedDate = message.createdAt
    ? dayjs(message.createdAt).format(
        dayjs(message.createdAt).year() === dayjs().year()
          ? 'MMM D'
          : 'MMM D, YYYY'
      )
    : '';

  const renderBody = () => {
    if (message.negotiationSnapshot) {
      return (
        <NegotiationSnapshotCard
          snapshot={message.negotiationSnapshot}
          onRespond={onRespondToNegotiation}
        />
      );
    }
    if (message.bodyHtml) {
      return (
        <Body
          size="md"
          color={designSystemToken('semantic.fg.primary')}
          component="div"
          className={styles.bodyHtml}
        >
          <div
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(message.bodyHtml),
            }} // eslint-disable-line react/no-danger
          />
        </Body>
      );
    }
    if (message.body) {
      return (
        <Body
          size="md"
          color={designSystemToken('semantic.fg.primary')}
          component="div"
          className={styles.body}
        >
          {message.body}
        </Body>
      );
    }
    return null;
  };

  return (
    <Stack
      direction="row"
      className={clsx(styles.card, !fromCurrentUser && styles.cardBrand)}
      data-testid="message-list-item"
      data-own={fromCurrentUser}
    >
      <Avatar
        size="sm"
        type={imageUrl ? 'picture' : 'initials'}
        src={imageUrl}
        initials={initialFor(message)}
      />
      <Stack className={styles.content}>
        <Stack direction="row" className={styles.header}>
          <Label size="md">{message.displayName}</Label>
          <Body size="sm" color={designSystemToken('semantic.fg.secondary')}>
            {formattedDate}
          </Body>
        </Stack>
        {renderBody()}
        {message.attachments.length > 0 && (
          <Stack direction="column">
            {message.attachments.map((attachment) => (
              <MessageAttachmentTile
                key={attachment.id}
                attachment={attachment}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Stack>
  );
}
